import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

/**
 * ChipInput — multi-value input that converts each entry into a chip on
 * Enter or comma. Used for coaches, partners, etc.
 *
 * Props:
 *   value      — array of strings (controlled)
 *   onChange   — (newArray) => void
 *   placeholder
 *   suggestions — optional string[] for autocomplete dropdown
 *   testId     — data-testid base for input + chips
 */
const ChipInput = ({ value = [], onChange, placeholder = 'Type and press Enter', suggestions = [], testId = 'chip-input' }) => {
  const [draft, setDraft] = useState('');

  const add = (raw) => {
    const v = (raw ?? draft).trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(''); return; }
    onChange([...value, v]);
    setDraft('');
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      remove(value.length - 1);
    }
  };

  // Filter suggestions to ones not already chipped + matching the draft
  const draftLower = draft.toLowerCase();
  const filteredSuggestions = draft
    ? suggestions.filter((s) => !value.includes(s) && s.toLowerCase().includes(draftLower)).slice(0, 6)
    : [];

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((chip, i) => (
          <span
            key={`${chip}-${i}`}
            data-testid={`${testId}-chip-${i}`}
            className="inline-flex items-center gap-1 px-2 py-1 bg-enf-accent/15 border border-enf-accent-fg/40 rounded-md text-xs text-enf-text-primary"
          >
            {chip}
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label={`Remove ${chip}`}
              className="text-enf-text-muted hover:text-enf-role-warning"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            data-testid={`${testId}-input`}
            className="flex-1 px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
          />
          <button
            type="button"
            onClick={() => add()}
            disabled={!draft.trim()}
            aria-label="Add"
            data-testid={`${testId}-add`}
            className="px-2 py-2 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-enf-text-secondary hover:text-enf-text-primary disabled:opacity-40 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {filteredSuggestions.length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-enf-surface-deep border border-enf-surface-border rounded-md shadow-lg max-h-40 overflow-y-auto">
            {filteredSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                className="block w-full text-left px-3 py-1.5 text-sm text-enf-text-secondary hover:bg-enf-accent hover:text-enf-text-primary"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChipInput;
